<script setup lang="ts">
import { ref, watch } from 'vue'
import { 
  VueFlow, 
  useVueFlow, 
  Handle, 
  Position, 
  BaseEdge, 
  EdgeLabelRenderer, 
  getBezierPath
} from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'
import { Button } from '@/components/ui/button'
import { PlusSquare, Database, Server, Cloud, HardDrive, Globe, Network, Trash2, RefreshCw, Box } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'save', mermaidCode: string): void
}>()

const nodes = ref<any[]>([])
const edges = ref<any[]>([])
const showCode = ref(false)
const copied = ref(false)
const editableCode = ref('')
const isParsing = ref(false)

const ARCHITECTURE_ICONS = [
  { id: 'cloud', name: 'Cloud', icon: Cloud },
  { id: 'database', name: 'Database', icon: Database },
  { id: 'disk', name: 'Disk / Storage', icon: HardDrive },
  { id: 'server', name: 'Server', icon: Server },
  { id: 'internet', name: 'Internet', icon: Globe },
]

const PORTS = ['L', 'R', 'T', 'B']

const initNodes = () => {
  nodes.value = [
    { 
      id: 'g_api', type: 'groupNode', position: { x: 50, y: 50 }, 
      data: { label: 'API Group', icon: 'cloud' } 
    },
    { 
      id: 's_db', type: 'serviceNode', position: { x: 50, y: 150 }, 
      data: { label: 'Database', icon: 'database', parentGroup: 'g_api' } 
    },
    { 
      id: 's_server', type: 'serviceNode', position: { x: 300, y: 150 }, 
      data: { label: 'Server', icon: 'server', parentGroup: 'g_api' } 
    }
  ]
  edges.value = [
    {
      id: 'e_s_db_s_server', source: 's_db', target: 's_server', type: 'custom',
      data: { sourcePort: 'R', targetPort: 'L' }
    }
  ]
}

watch(() => props.open, (val) => {
  if (!val) {
    nodes.value = []
    edges.value = []
    editableCode.value = ''
    return
  }

  if (val) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (val && nodes.value.length === 0) initNodes()
}, { immediate: true })

const { onConnect } = useVueFlow()

const handleConnect = (params: any) => {
  edges.value.push({
    id: `e-${params.source}-${params.target}-${Date.now()}`,
    source: params.source,
    target: params.target,
    type: 'custom',
    data: { 
      sourcePort: 'R',
      targetPort: 'L'
    }
  })
}

const addGroupNode = () => {
  const id = `g_group_${Date.now()}`
  nodes.value.push({
    id,
    type: 'groupNode',
    position: { x: Math.random() * 200 + 100, y: Math.random() * 200 + 100 },
    data: { label: `Grupo`, icon: 'cloud' }
  })
}

const addServiceNode = () => {
  const id = `s_service_${Date.now()}`
  nodes.value.push({
    id,
    type: 'serviceNode',
    position: { x: Math.random() * 200 + 100, y: Math.random() * 200 + 100 },
    data: { label: `Servicio`, icon: 'server', parentGroup: '' }
  })
}

const deleteNode = (id: string) => {
  nodes.value = nodes.value.filter(n => n.id !== id)
  edges.value = edges.value.filter(e => e.source !== id && e.target !== id)
}

const deleteEdge = (id: string) => {
  edges.value = edges.value.filter(e => e.id !== id)
}

const generateCodeString = () => {
  let mermaid = '\n```mermaid\n'
  
  const positions: Record<string, any> = {}
  nodes.value.forEach(node => {
    positions[node.id] = { x: Math.round(node.position.x), y: Math.round(node.position.y) }
  })
  mermaid += `%% {"positions": ${JSON.stringify(positions)}}\n`
  
  mermaid += `architecture-beta\n`
  
  const groups = nodes.value.filter(n => n.type === 'groupNode')
  const services = nodes.value.filter(n => n.type === 'serviceNode')

  groups.forEach(g => {
    mermaid += `    group ${g.id}(${g.data.icon || 'cloud'})[${g.data.label}]\n`
  })

  if (groups.length > 0) mermaid += '\n'

  services.forEach(s => {
    let line = `    service ${s.id}(${s.data.icon || 'server'})[${s.data.label}]`
    if (s.data.parentGroup) {
      line += ` in ${s.data.parentGroup}`
    }
    mermaid += line + '\n'
  })

  if (edges.value.length > 0) mermaid += '\n'

  edges.value.forEach(edge => {
    // Mermaid Architecture sólo permite conectar Servicios, no Grupos.
    const sourceNode = nodes.value.find(n => n.id === edge.source)
    const targetNode = nodes.value.find(n => n.id === edge.target)
    
    if (sourceNode?.type === 'serviceNode' && targetNode?.type === 'serviceNode') {
      const sPort = edge.data?.sourcePort || 'R'
      const tPort = edge.data?.targetPort || 'L'
      mermaid += `    ${edge.source}:${sPort} -- ${tPort}:${edge.target}\n`
    }
  })

  mermaid += '```\n'
  return mermaid
}

watch([nodes, edges], () => {
  if (isParsing.value) return
  editableCode.value = generateCodeString()
}, { deep: true, immediate: true })

const syncToVisual = () => {
  isParsing.value = true
  try {
    const lines = editableCode.value.split('\n')
    const newNodes: any[] = []
    const newEdges: any[] = []
    
    let xOffset = 50
    let yOffset = 50
    let savedPositions: Record<string, any> | null = null

    lines.forEach(line => {
      line = line.trim()
      
      // Parsear JSON oculto de posiciones
      if (line.startsWith('%% {"positions":')) {
        try {
          savedPositions = JSON.parse(line.replace(/^%% /, '')).positions
        } catch (e) {
          console.error('Error parseando metadata de posiciones', e)
        }
        return
      }

      if (!line || line.startsWith('```') || line.startsWith('%%') || line.startsWith('architecture-beta')) return

      // Parse Group
      const groupMatch = line.match(/^group\s+([a-zA-Z0-9_]+)\(([^)]+)\)\[([^\]]+)\]/)
      if (groupMatch) {
        const id = groupMatch[1]
        let pos = { x: xOffset, y: yOffset }
        if (savedPositions && savedPositions[id]) pos = savedPositions[id]
        else {
          const existing = nodes.value.find(n => n.id === id)
          if (existing) pos = existing.position
        }
        
        newNodes.push({
          id,
          type: 'groupNode',
          position: pos,
          data: { icon: groupMatch[2], label: groupMatch[3] }
        })
        if (!savedPositions || !savedPositions[id]) {
          xOffset += 250
          if (xOffset > 600) { xOffset = 50; yOffset += 200 }
        }
        return
      }

      // Parse Service
      const serviceMatch = line.match(/^service\s+([a-zA-Z0-9_]+)\(([^)]+)\)\[([^\]]+)\](?:\s+in\s+([a-zA-Z0-9_]+))?/)
      if (serviceMatch) {
        const id = serviceMatch[1]
        let pos = { x: xOffset, y: yOffset }
        if (savedPositions && savedPositions[id]) pos = savedPositions[id]
        else {
          const existing = nodes.value.find(n => n.id === id)
          if (existing) pos = existing.position
        }

        newNodes.push({
          id,
          type: 'serviceNode',
          position: pos,
          data: { 
            icon: serviceMatch[2], 
            label: serviceMatch[3],
            parentGroup: serviceMatch[4] || ''
          }
        })
        if (!savedPositions || !savedPositions[id]) {
          xOffset += 200
          if (xOffset > 800) { xOffset = 50; yOffset += 150 }
        }
        return
      }

      // Parse Edge
      const edgeMatch = line.match(/^([a-zA-Z0-9_]+):([LRTB])\s*--\s*([LRTB]):([a-zA-Z0-9_]+)/)
      if (edgeMatch) {
        newEdges.push({
          id: `e-${edgeMatch[1]}-${edgeMatch[4]}-${Date.now()}-${Math.random()}`,
          source: edgeMatch[1],
          target: edgeMatch[4],
          type: 'custom',
          data: {
            sourcePort: edgeMatch[2],
            targetPort: edgeMatch[3]
          }
        })
        return
      }
    })

    nodes.value = newNodes
    edges.value = newEdges

  } catch (error) {
    console.error("Error parsing architecture code:", error)
  } finally {
    setTimeout(() => { isParsing.value = false }, 100)
  }
}

const onCodeChange = () => {
  // Can implement debounce here
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (e) {
    console.error('Failed to copy', e)
  }
}

const generateMermaid = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Diagrama de Arquitectura"
    description="Diseña arquitecturas cloud. Crea Grupos y Servicios, y conéctalos especificando los puertos de anclaje (L, R, T, B)."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="generateMermaid"
  >
    <template #icon>
      <Network class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" @click="addGroupNode"><Box class="w-4 h-4 mr-2" /> Añadir Grupo</Button>
      <Button size="sm" @click="addServiceNode" variant="secondary"><Server class="w-4 h-4 mr-2" /> Añadir Servicio</Button>
    </template>

    <template #canvas>
      <VueFlow 
        v-model:nodes="nodes" 
        v-model:edges="edges"
        @connect="handleConnect"
        :default-zoom="1"
      >
        <Background />
        <Controls />

        <!-- Node: Group -->
        <template #node-groupNode="nodeProps">
          <div class="relative group border-2 border-primary/50 border-dashed rounded-lg bg-primary/5 shadow-sm min-w-[200px] p-3 flex flex-col font-sans">
            <div class="flex items-center gap-2 border-b border-primary/20 pb-2 mb-2">
              <select v-model="nodeProps.data.icon" class="text-xs border rounded p-1 bg-background outline-none">
                <option v-for="ic in ARCHITECTURE_ICONS" :key="ic.id" :value="ic.id">{{ ic.name }}</option>
              </select>
              <input v-model="nodeProps.data.label" class="bg-transparent font-bold w-full focus:outline-none" placeholder="Nombre Grupo" />
            </div>
            <div class="text-xs text-muted-foreground">
              ID: {{ nodeProps.id }}
            </div>
            
            <div class="absolute -top-3 -right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
              <button @click.stop="deleteNode(nodeProps.id)" class="bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center hover:scale-110 shadow-sm" title="Eliminar Grupo">×</button>
            </div>
          </div>
        </template>

        <!-- Node: Service -->
        <template #node-serviceNode="nodeProps">
          <div class="relative group border-2 border-primary rounded-md bg-card shadow-md flex flex-col min-w-[180px] p-2 font-sans">
            <div class="flex items-center gap-2 mb-2">
              <select v-model="nodeProps.data.icon" class="text-xs border rounded p-1 bg-background outline-none">
                <option v-for="ic in ARCHITECTURE_ICONS" :key="ic.id" :value="ic.id">{{ ic.name }}</option>
              </select>
              <input v-model="nodeProps.data.label" class="bg-transparent font-bold w-full focus:outline-none" placeholder="Servicio" />
            </div>
            <div class="flex flex-col gap-1 text-xs">
              <label class="text-muted-foreground">Grupo padre (ID opcional):</label>
              <select v-model="nodeProps.data.parentGroup" class="border rounded p-1 bg-background outline-none w-full">
                <option value="">(Ninguno)</option>
                <option v-for="g in nodes.filter(n => n.type === 'groupNode')" :key="g.id" :value="g.id">{{ g.data.label }} ({{ g.id }})</option>
              </select>
            </div>

            <Handle type="target" :position="Position.Top" />
            <Handle type="source" :position="Position.Bottom" />
            <Handle type="source" :position="Position.Right" />
            <Handle type="target" :position="Position.Left" />

            <div class="absolute -top-3 -right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
              <button @click.stop="deleteNode(nodeProps.id)" class="bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center hover:scale-110 shadow-sm" title="Eliminar Servicio">×</button>
            </div>
          </div>
        </template>

        <!-- Edge: Architecture Connection -->
        <template #edge-custom="edgeProps">
          <BaseEdge 
            :id="edgeProps.id" 
            :path="getBezierPath(edgeProps)[0]" 
            class="vue-flow__edge-path" 
          />
          <EdgeLabelRenderer>
            <div
              :style="{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${getBezierPath(edgeProps)[1]}px,${getBezierPath(edgeProps)[2]}px)`,
                pointerEvents: 'all',
              }"
              class="nodrag nopan bg-card border shadow-md rounded p-1 group z-10 flex gap-1 items-center"
            >
              <select v-model="edgeProps.data.sourcePort" class="text-[10px] border rounded bg-background focus:ring-1 focus:ring-primary outline-none" title="Puerto Origen">
                <option v-for="p in PORTS" :key="p" :value="p">{{ p }}</option>
              </select>
              <span class="text-[10px] text-muted-foreground">--</span>
              <select v-model="edgeProps.data.targetPort" class="text-[10px] border rounded bg-background focus:ring-1 focus:ring-primary outline-none" title="Puerto Destino">
                <option v-for="p in PORTS" :key="p" :value="p">{{ p }}</option>
              </select>

              <button 
                @click="deleteEdge(edgeProps.id)" 
                class="absolute -top-2 -right-2 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                title="Eliminar Conexión"
              >×</button>
            </div>
          </EdgeLabelRenderer>
        </template>
      </VueFlow>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>

<style>
.vue-flow__edge-path {
  stroke: hsl(var(--primary));
  stroke-width: 2;
}
.dark select {
  background-color: hsl(var(--background));
  color: hsl(var(--foreground));
  border-color: hsl(var(--border));
}
</style>
